/**
 * Hook: Export pipeline — table selection, validation, connector picking, and export execution.
 */
import { useState, useCallback, useMemo } from 'react';
import type { WorkspaceOperation, WorkspaceTable } from '../../../services/workspaceApi';
import { exportToConnector, validateExport } from '../../../services/connectorApi';
import { useNotifications } from '../../../context/NotificationContext';
import type { ConnectorConfig, TableValidationResult } from '../../../services/connectorApi';
import type { OutputTableInfo } from '../PipelineOutputsDrawer';
import type { MaterializationResult } from './useMappingWorkflow';
import { TRANSFORMATIVE_TYPES } from '../utils/pipelineNodeBuilder';
import { mappedOutputsFromWorkspaceTables } from '../utils/mappedOutputDiscovery';

function outputLabelForRef(ref: string, outputs: OutputTableInfo[]): string {
  const info = outputs.find((o) => o.outputRef === ref);
  if (info?.displayName) return info.displayName;
  if (info?.description) return info.description.replace(/^Mapped:\s*/i, '');
  if (ref.startsWith('__mapped_') && ref.endsWith('__')) {
    return ref.slice('__mapped_'.length, -2);
  }
  return ref;
}

function getEmptyExportLabels(
  selectedRefs: string[],
  outputs: OutputTableInfo[],
): string[] {
  const labels: string[] = [];
  for (const ref of selectedRefs) {
    const info = outputs.find((o) => o.outputRef === ref);
    if (!info) continue;
    if (ref.startsWith('assembly://')) {
      if (info.byteSize === 0) {
        labels.push(outputLabelForRef(ref, outputs));
      }
      continue;
    }
    if (info.rowCount === 0) {
      labels.push(outputLabelForRef(ref, outputs));
    }
  }
  return labels;
}

function formatEmptyExportMessage(labels: string[]): string {
  if (labels.length === 0) {
    return 'Cannot export: the selected output has no rows. Add data or choose a different output table.';
  }
  if (labels.length === 1) {
    return `Cannot export: "${labels[0]}" has no rows. Add data or choose a different output table.`;
  }
  return `Cannot export: ${labels.map((label) => `"${label}"`).join(', ')} have no rows. Add data or choose different output tables.`;
}

/* ── Quality Summary Types ───────────────────────────────────────────── */

export interface QualityCheck {
  name: string;            // e.g. "row_count_delta", "null_introduction", "match_rate"
  passed: boolean;
  measured?: number;       // e.g. 0.15 (15% delta)
  threshold?: number;      // e.g. 0.10
  operationDescription?: string;
}

export interface QualityWarning {
  severity: string;        // "info" | "warning" | "critical"
  message: string;
  checkName: string;
}

export interface OutputQualitySummary {
  overallStatus: 'pass' | 'warn' | 'fail' | 'no_review';
  failedChecks: QualityCheck[];
  passedCheckCount: number;
  warnings: QualityWarning[];
  correctionCount: number;
  correctionDescriptions: string[];
  rowCount?: number;
  rowCountBefore?: number;
  reviewPassed: boolean;
  reviewMissing: boolean;
}

/* ── Quality Aggregation ─────────────────────────────────────────────── */

function aggregateQualityForOutputs(
  operations: WorkspaceOperation[],
  completedOutputs: OutputTableInfo[],
): Map<string, OutputQualitySummary> {
  // No operations means no quality data to aggregate (pure mapping batch).
  // Return empty map so the UI doesn't show "No review" for every output.
  if (operations.length === 0) return new Map();

  const byId = new Map<string, WorkspaceOperation>();
  const byOutputRef = new Map<string, WorkspaceOperation>();
  operations.forEach((op) => {
    byId.set(op.operation_id, op);
    if (op.output_ref) byOutputRef.set(op.output_ref, op);
  });

  const result = new Map<string, OutputQualitySummary>();

  for (const output of completedOutputs) {
    // BFS upstream to collect all review data in the chain.
    // For synthetic mapped outputs (operationId like "mapped_patient"), resolve
    // the real operation via outputRef lookup so we can still walk upstream.
    const visited = new Set<string>();
    const queue: string[] = [];

    if (output.operationId.startsWith('mapped_')) {
      // Synthetic: look up by outputRef to find the real schema_map/transform op
      const realOp = byOutputRef.get(output.outputRef);
      if (realOp) {
        queue.push(realOp.operation_id);
      }
      // Also seed from input_refs of that real op (upstream transforms may have reviews)
      if (realOp?.input_refs) {
        for (const ref of realOp.input_refs) {
          const upstream = byOutputRef.get(ref);
          if (upstream) queue.push(upstream.operation_id);
        }
      }
    } else {
      queue.push(output.operationId);
    }
    const allChecks: QualityCheck[] = [];
    const allWarnings: QualityWarning[] = [];
    let correctionCount = 0;
    const correctionDescriptions: string[] = [];
    let anyReviewPresent = false;
    let allReviewsPassed = true;
    let earliestRowCount: number | undefined;

    while (queue.length > 0) {
      const opId = queue.shift()!;
      if (visited.has(opId)) continue;
      visited.add(opId);

      const op = byId.get(opId);
      if (!op) continue;

      // Track earliest row count for delta
      if (earliestRowCount === undefined && op.row_count_after != null) {
        earliestRowCount = op.row_count_after;
      }

      const review = (op as any).review_result;
      if (review) {
        anyReviewPresent = true;

        // Checks
        if (Array.isArray(review.checks)) {
          for (const check of review.checks) {
            allChecks.push({
              name: check.name || check.check_name || 'unknown',
              passed: check.passed ?? check.status === 'pass',
              measured: check.measured ?? check.actual,
              threshold: check.threshold ?? check.expected,
              operationDescription: op.description?.replace('[SOLUTION] ', ''),
            });
            if (!(check.passed ?? check.status === 'pass')) {
              allReviewsPassed = false;
            }
          }
        }

        // Warnings
        if (Array.isArray(review.warnings)) {
          for (const w of review.warnings) {
            allWarnings.push({
              severity: w.severity || 'warning',
              message: w.message || w.description || String(w),
              checkName: w.check_name || w.name || 'general',
            });
          }
        }

        // Corrections
        if (Array.isArray(review.corrections)) {
          correctionCount += review.corrections.length;
          for (const c of review.corrections) {
            const desc = c.description || c.summary || c.action;
            if (desc) correctionDescriptions.push(desc);
          }
        }
      }

      // Walk upstream
      if (op.input_refs) {
        for (const ref of op.input_refs) {
          const upstream = byOutputRef.get(ref);
          if (upstream) {
            queue.push(upstream.operation_id);
            // Track earliest row count
            if (upstream.row_count_after != null) {
              earliestRowCount = upstream.row_count_after;
            }
          }
        }
      }
    }

    // Derive row count — use correction after_metrics if available
    const producingOp = byId.get(output.operationId);
    let rowCount = output.rowCount;
    if (producingOp) {
      const review = (producingOp as any).review_result;
      if (review?.corrections?.length > 0) {
        const lastCorrection = review.corrections[review.corrections.length - 1];
        if (lastCorrection.after_metrics?.row_count != null) {
          rowCount = lastCorrection.after_metrics.row_count;
        }
      }
    }

    const failedChecks = allChecks.filter((c) => !c.passed);
    const passedCheckCount = allChecks.filter((c) => c.passed).length;

    let overallStatus: OutputQualitySummary['overallStatus'];
    if (!anyReviewPresent) {
      overallStatus = 'no_review';
    } else if (failedChecks.length > 0) {
      overallStatus = 'fail';
    } else if (allWarnings.length > 0) {
      overallStatus = 'warn';
    } else {
      overallStatus = 'pass';
    }

    result.set(output.outputRef, {
      overallStatus,
      failedChecks,
      passedCheckCount,
      warnings: allWarnings,
      correctionCount,
      correctionDescriptions,
      rowCount,
      rowCountBefore: earliestRowCount,
      reviewPassed: anyReviewPresent && allReviewsPassed,
      reviewMissing: !anyReviewPresent,
    });
  }

  return result;
}

interface UseExportPipelineArgs {
  batchId: string;
  sessionId: string;
  sessionMeta?: Record<string, any> | null;
  operations: WorkspaceOperation[];
  workspaceTables: WorkspaceTable[];
  materializationResult: MaterializationResult | null;
  updateMeta: { mutate: (meta: Record<string, any>) => void };
  /** Persisted pipeline steps (for assembly output discovery). */
  pipelineSteps?: import('../../../services/workspaceApi').PersistedPipelineStep[];
}

export function useExportPipeline({
  batchId,
  sessionId,
  sessionMeta,
  operations,
  workspaceTables,
  materializationResult,
  updateMeta,
  pipelineSteps = [],
}: UseExportPipelineArgs) {
  const { showToast } = useNotifications();
  const [connectorPickerOpen, setConnectorPickerOpen] = useState(false);
  const [outputsDrawerOpen, setOutputsDrawerOpen] = useState(false);
  const [exportSelectedTables, setExportSelectedTables] = useState<string[]>([]);
  const [exportValidationResults, setExportValidationResults] = useState<Map<string, TableValidationResult>>(new Map());
  const [exportColumnFilters, setExportColumnFilters] = useState<Record<string, string[]> | undefined>();
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [pendingExportConnector, setPendingExportConnector] = useState<ConnectorConfig | null>(null);
  const [pendingExportTables, setPendingExportTables] = useState<string[]>([]);
  const [validationDialogResults, setValidationDialogResults] = useState<TableValidationResult[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Compute completed outputs for export
  const completedOutputs: OutputTableInfo[] = useMemo(() => {
    const DONE_STATUSES = new Set(['executed', 'promoted', 'corrected', 'completed']);

    const isMappedOp = (op: WorkspaceOperation): boolean =>
      (op.input_refs || []).some((ref) => ref.startsWith('__mapped_'));

    // Pre-mapping terminal outputs
    const allCompleted = operations.filter(
      (op) =>
        TRANSFORMATIVE_TYPES.has(op.operation_type) &&
        op.operation_type !== 'schema_map' &&
        !op.exploratory &&
        DONE_STATUSES.has(op.status) &&
        op.output_ref &&
        !isMappedOp(op),
    );
    const consumedRefs = new Set<string>();
    operations.forEach((op) => {
      if (TRANSFORMATIVE_TYPES.has(op.operation_type) && op.operation_type !== 'schema_map' && !op.exploratory && op.status !== 'discarded' && !isMappedOp(op)) {
        (op.input_refs || []).forEach((r) => consumedRefs.add(r));
      }
    });
    // Deduplicate by output_ref — keep latest (last in array order)
    const preDedupMap = new Map<string, WorkspaceOperation>();
    allCompleted
      .filter((op) => !consumedRefs.has(op.output_ref!))
      .forEach((op) => preDedupMap.set(op.output_ref!, op));
    const preMapOutputs: OutputTableInfo[] = Array.from(preDedupMap.values()).map((op) => {
      const wt = workspaceTables.find((t) => t.name === op.output_ref);
      return {
        operationId: op.operation_id,
        outputRef: op.output_ref!,
        description: op.description?.replace('[SOLUTION] ', '') || op.operation_type.replace(/_/g, ' '),
        stepType: op.operation_type,
        rowCount: op.row_count_after ?? wt?.row_count ?? undefined,
        columnCount: wt?.column_count ?? undefined,
        phase: 'pre_mapping' as const,
      };
    });

    // Post-mapping outputs
    const postMapOutputs: OutputTableInfo[] = [];
    const hasPostMappingResults =
      materializationResult &&
      (materializationResult.viewDetails.length > 0 || materializationResult.failedViews.length > 0);
    if (hasPostMappingResults) {
      const supersededViews = new Set<string>();
      const mappedTransformOutputs: OutputTableInfo[] = [];

      const mappedOps = operations.filter(
        (op) =>
          TRANSFORMATIVE_TYPES.has(op.operation_type) &&
          !op.exploratory &&
          DONE_STATUSES.has(op.status) &&
          op.output_ref &&
          isMappedOp(op),
      );
      const mappedConsumed = new Set<string>();
      mappedOps.forEach((op) => {
        (op.input_refs || []).forEach((r) => mappedConsumed.add(r));
      });
      // Deduplicate mapped ops by output_ref — keep latest
      const mappedDedupMap = new Map<string, WorkspaceOperation>();
      mappedOps
        .filter((op) => !mappedConsumed.has(op.output_ref!))
        .forEach((op) => mappedDedupMap.set(op.output_ref!, op));
      Array.from(mappedDedupMap.values()).forEach((op) => {
        const wt = workspaceTables.find((t) => t.name === op.output_ref);
        mappedTransformOutputs.push({
          operationId: op.operation_id,
          outputRef: op.output_ref!,
          description: op.description?.replace('[SOLUTION] ', '') || 'Post-mapping transform',
          stepType: op.operation_type,
          rowCount: op.row_count_after ?? wt?.row_count ?? undefined,
          columnCount: wt?.column_count ?? undefined,
          phase: 'post_mapping' as const,
        });
        (op.input_refs || []).forEach((ref) => {
          if (ref.startsWith('__mapped_')) supersededViews.add(ref);
        });
      });

      materializationResult.viewDetails.forEach((v) => {
        if (supersededViews.has(v.view_name)) return;
        const wt = workspaceTables.find((t) => t.name === v.view_name);
        postMapOutputs.push({
          operationId: `mapped_${v.target_table}`,
          outputRef: v.view_name,
          displayName: v.target_table,
          description: `Mapped: ${v.target_table}`,
          stepType: 'mapped_output',
          rowCount: wt?.row_count,
          columnCount: v.column_count ?? wt?.column_count,
          phase: 'post_mapping' as const,
        });
      });

      // Include failed views that weren't corrected so they are visible in the outputs drawer
      const resolvedViewNames = new Set(materializationResult.viewDetails.map((v) => v.view_name));
      materializationResult.failedViews.forEach((fv) => {
        if (resolvedViewNames.has(fv.view)) return; // already corrected and in viewDetails
        postMapOutputs.push({
          operationId: `mapped_failed_${fv.target}`,
          outputRef: fv.view,
          displayName: fv.target,
          description: `Mapped: ${fv.target}`,
          stepType: 'mapped_output',
          phase: 'post_mapping' as const,
          error: fv.error,
        });
      });

      postMapOutputs.push(...mappedTransformOutputs);
    }

    // Materialization populates postMapOutputs in memory only. When it is
    // missing (page remount / iterative map_to_schema), fall back to the
    // persisted schema_map ops — same output shape as the viewDetails path.
    if (postMapOutputs.length === 0) {
      const seenMappedRefs: Record<string, true> = {};
      operations.forEach((op) => {
        if (op.operation_type !== 'schema_map' || !op.output_ref) return;
        if (!DONE_STATUSES.has(op.status)) return;
        if (seenMappedRefs[op.output_ref]) return;
        seenMappedRefs[op.output_ref] = true;
        const wt = workspaceTables.find((t) => t.name === op.output_ref);
        const displayName =
          (op.config as { target_schema_name?: string })?.target_schema_name
          || op.output_ref.replace(/^__mapped_/, '').replace(/__$/, '');
        postMapOutputs.push({
          operationId: op.operation_id,
          outputRef: op.output_ref,
          displayName,
          description: `Mapped: ${displayName}`,
          stepType: 'mapped_output',
          rowCount: op.row_count_after ?? wt?.row_count,
          columnCount: wt?.column_count,
          phase: 'post_mapping',
        });
      });
    }

    // Session resume / navigation: DuckDB may still have __mapped_*__ views
    // while in-memory materializationResult was lost and schema_map ops are
    // absent (engine mode). Discover outputs from the live workspace catalog.
    if (postMapOutputs.length === 0) {
      const rawTargetTables = sessionMeta?.target_tables;
      const targetTables = Array.isArray(rawTargetTables)
        ? rawTargetTables.filter((t): t is string => typeof t === 'string')
        : [];
      const mappedFromWorkspace = mappedOutputsFromWorkspaceTables(workspaceTables, targetTables);
      mappedFromWorkspace.forEach((entry) => {
        postMapOutputs.push({
          operationId: `mapped_ws_${entry.outputRef}`,
          outputRef: entry.outputRef,
          displayName: entry.displayName,
          description: `Mapped: ${entry.displayName}`,
          stepType: 'mapped_output',
          rowCount: entry.rowCount,
          columnCount: entry.columnCount,
          phase: 'post_mapping',
        });
      });
    }

    // Hierarchical assembly outputs — surfaced as selectable rows so the
    // user can pick the assembled XML/JSON as part of their Pipeline Output
    // selection. Source data: persisted pipeline steps with step_type
    // hierarchical_assembly + a fallback to workspace_meta.assembly_output_ref
    // for on-demand assemblies that don't create a step.
    const assemblyOutputs: OutputTableInfo[] = [];
    const seenAssemblyRefs = new Set<string>();
    for (const step of pipelineSteps) {
      if (step.step_type !== 'hierarchical_assembly' || !step.output_ref) continue;
      const ref = step.output_ref;
      if (!ref.startsWith('assembly://')) continue;
      seenAssemblyRefs.add(ref);
      const fmt = (step.metrics as any)?.output_format
        || ref.split('.').pop();
      const byteSize = typeof (step.metrics as any)?.byte_size === 'number'
        ? (step.metrics as any).byte_size : undefined;
      assemblyOutputs.push({
        operationId: step.step_id,
        outputRef: ref,
        displayName: ref.split('/').pop() || ref,
        description: 'Hierarchical output',
        stepType: 'hierarchical_assembly',
        rowCount: step.row_count ?? undefined,
        assembledOutputPath: ref,
        outputFormat: fmt,
        byteSize,
      });
    }
    const metaAssemblyRef = (sessionMeta as any)?.assembly_output_ref;
    if (typeof metaAssemblyRef === 'string'
      && metaAssemblyRef.startsWith('assembly://')
      && !seenAssemblyRefs.has(metaAssemblyRef)) {
      assemblyOutputs.push({
        operationId: `meta_assembly_${metaAssemblyRef}`,
        outputRef: metaAssemblyRef,
        displayName: metaAssemblyRef.split('/').pop() || metaAssemblyRef,
        description: 'Hierarchical output',
        stepType: 'hierarchical_assembly',
        rowCount: (sessionMeta as any)?.assembly_row_count,
        assembledOutputPath: metaAssemblyRef,
        outputFormat: (sessionMeta as any)?.assembly_format,
        byteSize: (sessionMeta as any)?.assembly_byte_size,
      });
    }

    const tabularOutputs = [...postMapOutputs, ...preMapOutputs];
    return [...tabularOutputs, ...assemblyOutputs];
  }, [operations, workspaceTables, materializationResult, pipelineSteps, sessionMeta]);

  // Quality summaries for all completed outputs
  const qualitySummaries = useMemo(
    () => aggregateQualityForOutputs(operations, completedOutputs),
    [operations, completedOutputs],
  );

  const handleViewAllOutputs = useCallback(() => {
    setOutputsDrawerOpen(true);
  }, []);

  const blockEmptyExportSelections = useCallback((selectedRefs: string[]): boolean => {
    const emptyLabels = getEmptyExportLabels(selectedRefs, completedOutputs);
    if (emptyLabels.length === 0) return false;
    const message = formatEmptyExportMessage(emptyLabels);
    setValidationError(message);
    showToast('Nothing to export', message, 'warning');
    updateMeta.mutate({ destination_status: 'pending' });
    return true;
  }, [completedOutputs, showToast, updateMeta]);

  const [assemblyExportMode, setAssemblyExportMode] = useState(false);
  const [assemblyExportFormat, setAssemblyExportFormat] = useState<string | undefined>();

  const handleExportSelectedOutputs = useCallback((outputRefs: string[], columnFilters?: Record<string, string[]>) => {
    setExportSelectedTables(outputRefs);
    setExportColumnFilters(columnFilters);
    // Detect if all selected outputs are assembly refs
    const allAssembly = outputRefs.length > 0 && outputRefs.every((r) => r.startsWith('assembly://'));
    setAssemblyExportMode(allAssembly);
    if (allAssembly) {
      const matchedOutput = completedOutputs.find((o) => o.assembledOutputPath && outputRefs.includes(o.assembledOutputPath));
      setAssemblyExportFormat(matchedOutput?.outputFormat || 'xml');
    } else {
      setAssemblyExportFormat(undefined);
    }
    setOutputsDrawerOpen(false);
    setConnectorPickerOpen(true);
  }, [completedOutputs]);

  const handleExport = useCallback(() => {
    if (completedOutputs.length > 0 && exportSelectedTables.length === 0) {
      setExportSelectedTables(completedOutputs.map((o) => o.outputRef));
    }
    // Route through the outputs drawer so the user sees quality data before exporting
    setOutputsDrawerOpen(true);
  }, [completedOutputs, exportSelectedTables]);

  const handleConnectorSelect = useCallback(
    async (connector: ConnectorConfig, targetTable: string, _columnsByTable?: Record<string, string[]>) => {
      setConnectorPickerOpen(false);
      setValidationError(null);

      if (exportSelectedTables.length > 0) {
        if (blockEmptyExportSelections(exportSelectedTables)) {
          setExportSelectedTables([]);
          setExportColumnFilters(undefined);
          return;
        }

        updateMeta.mutate({
          destination_connector_id: connector.connector_id,
          destination_connector_name: connector.name,
          destination_connector_type: connector.connector_type,
          destination_table: exportSelectedTables.join(', '),
          destination_status: 'validating',
        });

        const exportTables = exportSelectedTables.map((ref) =>
          ref.startsWith('__mapped_') && ref.endsWith('__')
            ? ref.slice(9, -2)
            : ref,
        );

        const tables = exportSelectedTables.map((ref, i) => ({
          source_table: ref,
          target_table: exportTables[i],
          source_columns: [] as Array<{ name: string; dtype: string }>,
        }));

        try {
          const validationResponse = await validateExport(connector.connector_id, tables, sessionId);
          const resultsMap = new Map<string, TableValidationResult>();
          validationResponse.results.forEach((r) => resultsMap.set(r.table, r));
          setExportValidationResults(resultsMap);

          const hasErrors = validationResponse.results.some((r) => r.status === 'fail');
          const hasWarnings = validationResponse.results.some((r) => r.status === 'warn');

          if (hasErrors || hasWarnings) {
            setPendingExportConnector(connector);
            setPendingExportTables(exportTables);
            setValidationDialogResults(validationResponse.results);
            setValidationDialogOpen(true);
            return;
          }
        } catch (validationErr: any) {
          setValidationError(validationErr?.message || 'Pre-export validation failed — cannot verify destination compatibility');
          updateMeta.mutate({ destination_status: 'pending' });
          return; // Block export — do not proceed silently
        }

        // Backend is the single writer of ``destination_status`` from here
        // on — we do NOT pre-emptively mutate session meta. The backend
        // transitions ``validating`` → ``exporting`` atomically in its
        // export handler (prompt-orchestrator workspace.py) and onwards
        // to ``exported`` / ``failed`` from its background thread.
        try {
          await exportToConnector(
            batchId,
            connector.connector_id,
            sessionId,
            exportTables,
            exportColumnFilters,
          );
          // Export accepted — completion/failure arrives via SSE → cache invalidation
        } catch (err: any) {
          if (err?.response?.status === 409) {
            showToast('Export in progress', 'An export is already running for this batch.', 'warning');
          } else {
            showToast('Export failed to start', err?.message || 'Unable to start export.', 'error');
          }
        }
        setExportSelectedTables([]);
        setExportColumnFilters(undefined);
        return;
      }

      // Standard single-destination flow
      updateMeta.mutate({
        destination_connector_id: connector.connector_id,
        destination_connector_name: connector.name,
        destination_connector_type: connector.connector_type,
        destination_table: targetTable || undefined,
        destination_status: 'pending',
      });
    },
    [updateMeta, batchId, exportSelectedTables, exportColumnFilters, sessionId, blockEmptyExportSelections, showToast],
  );

  const handleValidationExportAnyway = useCallback(async () => {
    setValidationDialogOpen(false);
    if (!pendingExportConnector) return;

    if (exportSelectedTables.length > 0 && blockEmptyExportSelections(exportSelectedTables)) {
      setExportSelectedTables([]);
      setExportColumnFilters(undefined);
      setPendingExportConnector(null);
      setPendingExportTables([]);
      return;
    }

    // Backend owns destination_status transitions — do not pre-emptively
    // mutate session meta here.
    try {
      await exportToConnector(
        batchId,
        pendingExportConnector.connector_id,
        sessionId,
        pendingExportTables,
        exportColumnFilters,
      );
      // Export accepted — completion/failure arrives via SSE → cache invalidation
    } catch (err: any) {
      if (err?.response?.status === 409) {
        showToast('Export in progress', 'An export is already running for this batch.', 'warning');
      } else {
        showToast('Export failed to start', err?.message || 'Unable to start export.', 'error');
      }
    }
    setExportSelectedTables([]);
    setExportColumnFilters(undefined);
    setPendingExportConnector(null);
    setPendingExportTables([]);
  }, [pendingExportConnector, pendingExportTables, batchId, exportColumnFilters, sessionId, showToast, exportSelectedTables, blockEmptyExportSelections]);

  const handleStartExport = useCallback(async () => {
    const connectorId = sessionMeta?.destination_connector_id;
    if (!connectorId) return;

    // Backend owns destination_status transitions — do not pre-emptively
    // mutate session meta here.
    try {
      const targetTable = sessionMeta?.destination_table;
      // destination_table may be comma-joined for multi-table exports (e.g. "a, b, c")
      const targetTables = targetTable
        ? targetTable.split(', ').filter(Boolean)
        : undefined;
      await exportToConnector(
        batchId,
        connectorId,
        sessionId,
        targetTables,
        undefined,
      );
      // Export accepted — completion/failure arrives via SSE → cache invalidation
    } catch (err: any) {
      if (err?.response?.status === 409) {
        showToast('Export in progress', 'An export is already running for this batch.', 'warning');
      } else {
        showToast('Export failed to start', err?.message || 'Unable to start export.', 'error');
      }
    }
  }, [batchId, sessionMeta, sessionId, showToast]);

  // ── On-demand hierarchical assembly + download ─────────────────
  const [assembling, setAssembling] = useState(false);

  const handleAssembleAndDownload = useCallback(async () => {
    if (!sessionId) return;
    setAssembling(true);
    try {
      const { assembleOutput, downloadAssemblyOutput } = await import('../../../services/workspaceApi');
      const result = await assembleOutput(sessionId);
      if (result.output_ref) {
        await downloadAssemblyOutput(sessionId, result.output_ref);
        showToast(
          'XML exported',
          `${result.format.toUpperCase()} output (${(result.byte_size / 1024).toFixed(1)} KB, ${result.row_count} rows)`,
          'success',
        );
      }
    } catch (err: any) {
      showToast('Assembly failed', err?.message || 'Failed to assemble hierarchical output', 'error');
    } finally {
      setAssembling(false);
    }
  }, [sessionId, showToast]);

  return {
    completedOutputs,
    qualitySummaries,
    validationError,
    connectorPickerOpen,
    outputsDrawerOpen,
    exportSelectedTables,
    exportValidationResults,
    validationDialogOpen,
    validationDialogResults,
    pendingExportConnector,
    handleViewAllOutputs,
    handleExportSelectedOutputs,
    handleExport,
    handleConnectorSelect,
    handleValidationExportAnyway,
    handleStartExport,
    assembling,
    assemblyExportMode,
    assemblyExportFormat,
    handleAssembleAndDownload,
    closeConnectorPicker: useCallback(() => { setConnectorPickerOpen(false); setExportSelectedTables([]); setAssemblyExportMode(false); }, []),
    closeOutputsDrawer: useCallback(() => setOutputsDrawerOpen(false), []),
    closeValidationDialog: useCallback(() => {
      setValidationDialogOpen(false);
      setPendingExportConnector(null);
      setPendingExportTables([]);
      updateMeta.mutate({ destination_status: 'pending' });
    }, [updateMeta]),
    clearValidationError: useCallback(() => setValidationError(null), []),
  };
}
